// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Settings > Notifications > the sources that can notify through Konvo, with their logos,
// one-line hints and master switches. The phone lists them (PhoneNotificationsTab), the desktop
// table puts a row per source (NotificationMatrix); KonvoNotificationPrefs says what each gates.

import KonvoNotificationPrefs, {
	type KonvoNotificationSource,
} from '@app/features/konvo_notifications/state/KonvoNotificationPrefs';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {TeamsLogo} from '@app/features/ui/components/icons/TeamsLogo';
import {WhatsAppLogo} from '@app/features/ui/components/icons/WhatsAppLogo';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import type React from 'react';
import {useEffect} from 'react';

const KONVO_CHATS_DESCRIPTOR = msg({
	message: 'Konvo chats',
	comment:
		'Switch in Settings > Notifications > Sources: notifications for direct messages and groups between Konvo accounts.',
});
const TEAMS_DESCRIPTOR = msg({
	message: 'Teams chats',
	comment: 'Switch in Settings > Notifications > Sources: notifications for chats that live in Microsoft Teams.',
});
const WHATSAPP_DESCRIPTOR = msg({
	message: 'WhatsApp',
	comment: 'Switch in Settings > Notifications > Sources: notifications for the linked WhatsApp phone.',
});
const MAIL_DESCRIPTOR = msg({
	message: 'Mail',
	comment: 'Switch in Settings > Notifications > Sources: notifications for new Outlook mail.',
});
const CLICKUP_DESCRIPTOR = msg({
	message: 'ClickUp',
	comment:
		'Switch in Settings > Notifications > Sources: notifications for activity on ClickUp tasks assigned to you (comments, status, due date).',
});

const KONVO_CHATS_HINT_DESCRIPTOR = msg({
	message: 'Direct messages and groups',
	comment: 'Phone Settings > Notifications > Sources: one line under "Konvo chats" saying what it covers.',
});
const TEAMS_HINT_DESCRIPTOR = msg({
	message: 'Chats mirrored from Teams',
	comment: 'Phone Settings > Notifications > Sources: one line under "Teams chats" saying what it covers.',
});
const WHATSAPP_HINT_DESCRIPTOR = msg({
	message: 'Your linked phone',
	comment: 'Phone Settings > Notifications > Sources: one line under "WhatsApp" saying what it covers.',
});
const MAIL_HINT_DESCRIPTOR = msg({
	message: 'New mail in your inbox',
	comment: 'Phone Settings > Notifications > Sources: one line under "Mail" saying what it covers.',
});
const CLICKUP_HINT_DESCRIPTOR = msg({
	message: 'Activity on your tasks',
	comment: 'Phone Settings > Notifications > Sources: one line under "ClickUp" saying what it covers.',
});
export const SOURCE_OFF_DESCRIPTOR = msg({
	message: 'Off',
	comment: 'Phone Settings > Notifications > Sources: shown under a source whose notifications are switched off.',
});

export interface NotificationSourceItem {
	key: 'konvo-chats' | 'teams' | 'whatsapp' | 'mail' | 'clickup';
	label: string;
	hint: string;
	source: KonvoNotificationSource;
	logo: React.ReactNode;
	value: boolean;
	onChange: (value: boolean) => void;
}

/** The connected sources that can notify, with their logos and switches (loads the prefs once). */
export function useNotificationSourceItems(
	logoSize = MobileLayout.enabled ? 26 : 22,
): ReadonlyArray<NotificationSourceItem> {
	const {i18n} = useLingui();
	useEffect(() => {
		void KonvoNotificationPrefs.ensureLoaded();
	}, []);
	return [
		{
			key: 'konvo-chats',
			label: i18n._(KONVO_CHATS_DESCRIPTOR),
			hint: i18n._(KONVO_CHATS_HINT_DESCRIPTOR),
			logo: <KonvoLogo size={logoSize} />,
			value: KonvoNotificationPrefs.konvoChats,
			source: 'konvo_chats',
			onChange: (value) => void KonvoNotificationPrefs.setSource('konvo_chats', value),
		},
		{
			key: 'teams',
			label: i18n._(TEAMS_DESCRIPTOR),
			hint: i18n._(TEAMS_HINT_DESCRIPTOR),
			logo: <TeamsLogo size={logoSize} />,
			value: KonvoNotificationPrefs.teams,
			source: 'teams',
			onChange: (value) => void KonvoNotificationPrefs.setSource('teams', value),
		},
		{
			key: 'whatsapp',
			label: i18n._(WHATSAPP_DESCRIPTOR),
			hint: i18n._(WHATSAPP_HINT_DESCRIPTOR),
			logo: <WhatsAppLogo size={logoSize} />,
			value: KonvoNotificationPrefs.whatsapp,
			source: 'whatsapp',
			onChange: (value) => void KonvoNotificationPrefs.setSource('whatsapp', value),
		},
		{
			key: 'mail',
			label: i18n._(MAIL_DESCRIPTOR),
			hint: i18n._(MAIL_HINT_DESCRIPTOR),
			logo: <OutlookLogo size={logoSize} />,
			value: KonvoNotificationPrefs.mail,
			source: 'mail',
			onChange: (value) => void KonvoNotificationPrefs.setSource('mail', value),
		},
		{
			key: 'clickup',
			label: i18n._(CLICKUP_DESCRIPTOR),
			hint: i18n._(CLICKUP_HINT_DESCRIPTOR),
			logo: <ClickUpLogo size={logoSize} />,
			value: KonvoNotificationPrefs.clickup,
			source: 'clickup',
			onChange: (value) => void KonvoNotificationPrefs.setSource('clickup', value),
		},
	];
}
